import React, { useState, useEffect } from 'react';
import { Activity, Calendar, Filter, X, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, MapPin, User } from 'lucide-react';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://fleet.host/api'
  : '/api';

interface Vehicle {
  vehicle_id: number | null;
  license_plate: string;
  nickname: string;
  location?: string;
  driver?: {
    driver_id: number;
    first_name: string;
    last_name: string;
  } | null;
}

interface EventDefinition {
  event_id: number;
  event_name: string;
  icon_id?: number;
  default_status_id?: number;
  is_active?: boolean;
}

interface Event {
  event_id: number;
  event_type: string;
  event_name: string;
  vehicle_count: number;
  timestamp: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  icon_id?: number;
  is_summary: boolean;
}

interface DetailedEventsResponse {
  success: boolean;
  data: {
    events: Event[];
    vehicles: Vehicle[];
    event_definitions: EventDefinition[];
    from_date: string;
    to_date: string;
    summary: {
      total_events: number;
      speeding_events: number;
      active_vehicles: number;
    };
  };
}

interface EventsTimelineProps {
  onClose: () => void;
}

const EventsTimeline: React.FC<EventsTimelineProps> = ({ onClose }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [eventDefinitions, setEventDefinitions] = useState<EventDefinition[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [eventVehicles, setEventVehicles] = useState<{ [eventId: number]: Vehicle[] }>({});
  const [loadingEventVehicles, setLoadingEventVehicles] = useState<{ [eventId: number]: boolean }>({});
  
  // Filters
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 7*24*60*60*1000).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/ituran-detailed-events.php?from_date=${fromDate}&to_date=${toDate}`);
      const result: DetailedEventsResponse = await response.json();
      
      if (result.success && result.data) {
        let eventsData = result.data.events || [];
        
        // Apply event type filters if selected
        if (selectedEventTypes.length > 0) {
          eventsData = eventsData.filter((event: Event) => 
            selectedEventTypes.includes(event.event_type)
          );
        }
        
        // Sort by vehicle count (most active first) then by severity
        eventsData.sort((a, b) => {
          if (b.vehicle_count !== a.vehicle_count) {
            return b.vehicle_count - a.vehicle_count;
          }
          const severityOrder = { high: 3, medium: 2, low: 1 };
          return severityOrder[b.severity] - severityOrder[a.severity];
        });
        
        setEvents(eventsData);
        setEventDefinitions(result.data.event_definitions || []);
        setSummary(result.data.summary);
      } else {
        setEvents([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [fromDate, toDate, selectedEventTypes]);

  const fetchEventVehicles = async (eventId: number) => {
    // If already loaded, skip
    if (eventVehicles[eventId]) return;
    
    setLoadingEventVehicles(prev => ({ ...prev, [eventId]: true }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/ituran-event-vehicles.php?event_id=${eventId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.vehicles && Array.isArray(result.vehicles)) {
        setEventVehicles(prev => ({ ...prev, [eventId]: result.vehicles }));
      } else {
        console.warn('Invalid response format for event', eventId, result);
        setEventVehicles(prev => ({ ...prev, [eventId]: [] }));
      }
    } catch (error) {
      console.error('Failed to fetch event vehicles:', error);
      setEventVehicles(prev => ({ ...prev, [eventId]: [] }));
    } finally {
      setLoadingEventVehicles(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const toggleEventExpansion = (eventId: number) => {
    const isExpanding = !expandedEvents.has(eventId);
    
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
    
    // Fetch vehicles when expanding
    if (isExpanding) {
      fetchEventVehicles(eventId);
    }
  };

  const toggleEventType = (eventName: string) => {
    setSelectedEventTypes(prev =>
      prev.includes(eventName)
        ? prev.filter(t => t !== eventName)
        : [...prev, eventName]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-orange-600 to-orange-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-xl font-bold text-white">Events Timeline</h2>
                <p className="text-sm text-orange-100">Track and filter fleet events</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${
                  showFilters ? 'bg-orange-500' : 'bg-orange-500 hover:bg-orange-400'
                } text-white`}
                title="Toggle filters"
              >
                <Filter className="w-5 h-5" />
              </button>
              <button
                onClick={fetchEvents}
                disabled={loading}
                className="p-2 bg-orange-500 hover:bg-orange-400 rounded-lg transition-colors text-white"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-orange-500 hover:bg-orange-400 rounded-lg transition-colors text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Date Range Filters */}
          <div className="mt-3 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-white" />
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                max={toDate}
                className="px-3 py-1.5 rounded text-sm border-0 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
            <span className="text-white">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate}
              max={new Date().toISOString().split('T')[0]}
              className="px-3 py-1.5 rounded text-sm border-0 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
            <div className="ml-auto text-white text-sm">
              {events.length} events
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900 mb-3">Filter by Event Type</h3>
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
              {eventDefinitions.map((def) => (
                <button
                  key={def.event_id}
                  onClick={() => toggleEventType(def.event_name)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedEventTypes.includes(def.event_name)
                      ? 'bg-orange-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {def.event_name}
                </button>
              ))}
              {selectedEventTypes.length > 0 && (
                <button
                  onClick={() => setSelectedEventTypes([])}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No events found for the selected period
            </div>
          ) : (
            <div className="space-y-3">
              {/* Summary Stats */}
              {summary && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200">
                    <div className="text-sm text-orange-700 font-medium">Total Events</div>
                    <div className="text-2xl font-bold text-orange-900">{summary.total_events}</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <div className="text-sm text-blue-700 font-medium">Active Vehicles</div>
                    <div className="text-2xl font-bold text-blue-900">{summary.active_vehicles}</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200">
                    <div className="text-sm text-red-700 font-medium">Speeding Events</div>
                    <div className="text-2xl font-bold text-red-900">{summary.speeding_events}</div>
                  </div>
                </div>
              )}

              {/* Event Cards */}
              {events.map((event) => {
                const isExpanded = expandedEvents.has(event.event_id);
                
                return (
                  <div
                    key={event.event_id}
                    className="border border-gray-200 rounded-lg hover:border-orange-300 transition-colors overflow-hidden"
                  >
                    {/* Event Header - Clickable */}
                    <button
                      onClick={() => toggleEventExpansion(event.event_id)}
                      className="w-full p-4 flex items-center justify-between hover:bg-orange-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {/* Severity Indicator */}
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                          event.severity === 'high' ? 'bg-red-500' :
                          event.severity === 'medium' ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }`}></div>
                        
                        {/* Event Info */}
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{event.event_name}</div>
                          <div className="text-sm text-gray-600 mt-1">{event.description}</div>
                        </div>
                        
                        {/* Badges */}
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                            {event.vehicle_count} vehicle{event.vehicle_count !== 1 ? 's' : ''}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                            event.severity === 'high' ? 'bg-red-100 text-red-700' :
                            event.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {event.severity}
                          </span>
                        </div>
                        
                        {/* Expand Icon */}
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                    
                    {/* Expanded Vehicle List */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50">
                        <div className="p-4">
                          {loadingEventVehicles[event.event_id] ? (
                            <div className="text-center py-4 text-gray-500">
                              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                              Loading vehicles...
                            </div>
                          ) : eventVehicles[event.event_id]?.length > 0 ? (
                            <>
                              <div className="font-medium text-gray-700 mb-3 text-sm">
                                Vehicles with this event ({eventVehicles[event.event_id].length})
                              </div>
                              <div className="space-y-2">
                                {eventVehicles[event.event_id].map((vehicle, idx) => (
                                  <div 
                                    key={vehicle.license_plate + idx}
                                    className="bg-white rounded-lg p-3 border border-gray-200 hover:border-orange-300 transition-colors"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex items-center gap-3 flex-1">
                                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                          <Activity className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-gray-900">{vehicle.license_plate}</div>
                                          {vehicle.nickname && vehicle.nickname !== vehicle.license_plate && (
                                            <div className="text-sm text-gray-600">{vehicle.nickname}</div>
                                          )}
                                          {vehicle.driver && (
                                            <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                                              <User className="w-3 h-3" />
                                              <span>{vehicle.driver.first_name} {vehicle.driver.last_name}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {vehicle.location && (
                                        <div className="flex items-center gap-1 text-xs text-gray-500 ml-2">
                                          <MapPin className="w-3 h-3" />
                                          <span>{vehicle.location}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              {eventVehicles[event.event_id] ? 'No vehicles found' : 'Click to load vehicle details'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsTimeline;
